import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Instagram, Linkedin, Youtube, Link2, CheckCircle2, XCircle, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const ICONS = { instagram: Instagram, linkedin: Linkedin, youtube: Youtube };
const BLURBS = {
  instagram: "Pull reels, stories, carousels + insights.",
  linkedin: "Sync post reach, comments, and profile visits.",
  youtube: "Ingest shorts + long-form watch time and CTR.",
};

export default function Connections() {
  const [conns, setConns] = useState([]);
  const [busy, setBusy] = useState({});
  const [params, setParams] = useSearchParams();

  const load = async () => {
    const { data } = await api.get("/api/connections");
    setConns(data);
  };
  useEffect(() => { load(); }, []);

  useEffect(() => {
    const err = params.get("connect_error");
    const ok = params.get("connected");
    if (err) {
      toast.error(`Connection failed: ${err}`);
      params.delete("connect_error"); params.delete("platform");
      setParams(params, { replace: true });
    } else if (ok) {
      toast.success(`${ok} connected`);
      params.delete("connected");
      setParams(params, { replace: true });
      load();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connect = async (platform) => {
    setBusy((b) => ({ ...b, [platform]: true }));
    try {
      const { data } = await api.post(`/api/connections/${platform}/authorize`);
      window.location.href = data.authorize_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cannot start OAuth");
      setBusy((b) => ({ ...b, [platform]: false }));
    }
  };

  const disconnect = async (platform) => {
    await api.delete(`/api/connections/${platform}`);
    toast.success(`${platform} disconnected`);
    await load();
  };

  return (
    <div className="min-h-screen">
      <div className="border-b border-border">
        <div className="px-8 py-8">
          <div className="text-xs uppercase tracking-widest text-muted-foreground font-bold">Social connections</div>
          <h1 className="mt-1 font-display font-black text-4xl tracking-tighter">Wire the analyst to your accounts.</h1>
          <p className="mt-2 text-muted-foreground max-w-2xl">Connect Instagram, LinkedIn, and YouTube so ContentOS pulls post-level metrics automatically. Manual import always remains an option.</p>
        </div>
      </div>

      <div className="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {conns.map((c) => {
          const Icon = ICONS[c.platform] || Link2;
          const status = !c.configured ? "not-configured" : c.connected ? "connected" : "disconnected";
          return (
            <div key={c.platform} className="border border-border rounded p-6 bg-card flex flex-col" data-testid={`conn-card-${c.platform}`}>
              <div className="flex items-start justify-between">
                <Icon className="h-6 w-6" strokeWidth={1.75}/>
                {status === "connected" && <span className="pillar-badge text-primary border-primary/40"><CheckCircle2 className="h-3 w-3"/> Connected</span>}
                {status === "disconnected" && <span className="pillar-badge text-muted-foreground"><XCircle className="h-3 w-3"/> Not connected</span>}
                {status === "not-configured" && <span className="pillar-badge text-destructive border-destructive/30"><AlertTriangle className="h-3 w-3"/> Not configured</span>}
              </div>
              <div className="mt-6">
                <div className="font-display font-bold text-2xl tracking-tight">{c.label}</div>
                <div className="mt-1 text-sm text-muted-foreground leading-relaxed">{BLURBS[c.platform] || ""}</div>
              </div>
              {c.account_name && (
                <div className="mt-4 text-sm">
                  <div className="font-mono text-xs uppercase text-muted-foreground">Account</div>
                  <div className="font-semibold">{c.account_name}</div>
                </div>
              )}
              <div className="mt-6 pt-4 border-t border-border">
                {status === "connected" ? (
                  <Button
                    variant="outline" className="w-full"
                    onClick={() => disconnect(c.platform)}
                    data-testid={`btn-disconnect-${c.platform}`}
                  >Disconnect</Button>
                ) : status === "disconnected" ? (
                  <Button
                    className="w-full"
                    onClick={() => connect(c.platform)}
                    disabled={busy[c.platform]}
                    data-testid={`btn-connect-${c.platform}`}
                  >
                    {busy[c.platform] ? <><Loader2 className="h-4 w-4 animate-spin"/> Opening…</> : <>Connect {c.label}</>}
                  </Button>
                ) : (
                  <div className="text-xs text-muted-foreground leading-relaxed">
                    Add credentials in <span className="font-mono text-foreground">backend/.env</span> to enable this platform, then complete the platform's app review to pull live data.
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div className="px-8 pb-10">
        <div className="border border-border rounded p-6 bg-secondary/40 max-w-3xl">
          <div className="font-mono text-xs uppercase tracking-widest text-muted-foreground">Heads up</div>
          <div className="mt-2 text-sm leading-relaxed">
            Each platform requires a developer app + review before it will return public post insights. The OAuth handshake, token storage, and refresh scaffolding are already wired — plug in your app credentials any time and the connect button lights up.
          </div>
        </div>
      </div>
    </div>
  );
}
